import { Button, ButtonVariant } from "components/Form/Button";
import { Layout } from "components/Layout";
import { memo, useCallback, useContext, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "types";
import { ReactComponent as Ai } from "assets/ai.svg";
import { Field } from "components/Form/Field";
import { InputPassword } from "components/Form/InputPassword";
import { Storage, StorageKey } from "lib/storage";
import { getUserEmail } from "lib/get-user-email";
import { ApiError, apiValidatePassphrase } from "lib/api";
import { AuthContext } from "contexts/AuthContext";
import { toast } from "react-toastify";
import { useSearchParamsPassphrase } from "hooks/useSearchParamsPassphrase";

type FormValue = {
  passphrase: string;
};

export const Welcome = memo(function Welcome() {
  const navigate = useNavigate();
  const passphrase = useSearchParamsPassphrase();
  const [processing, setProcessing] = useState(false);
  const userName = getUserEmail().split("@")[0];
  const { setToken } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setError,
  } = useForm<FormValue>({
    defaultValues: {
      passphrase,
    },
  });

  const onSubmit = useCallback<SubmitHandler<FormValue>>(
    async ({ passphrase }) => {
      if (isValid) {
        setProcessing(true);

        try {
          // Validate passphrase with backend and get updated JWT with contributor
          const token = await apiValidatePassphrase(passphrase);

          if (token) {
            // Store the updated token with contributor
            Storage.set(StorageKey.TOKEN, token);
            setToken(token);

            // Mark passphrase as validated
            Storage.set(StorageKey.PASSPHRASE, "validated");

            // Navigate to home
            navigate(AppRoutes.home, { replace: true });
            window.location.reload();
          }
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.attr === "passphrase") {
              setError("passphrase", {
                type: "custom",
                message:
                  "Invalid login credentials. Please check and try again.",
              });
            } else {
              toast.error(
                "Unable to validate passphrase, please try again later."
              );
            }
          } else {
            setError("passphrase", {
              type: "custom",
              message: "An error occurred. Please try again.",
            });
          }
        } finally {
          setProcessing(false);
        }
      }
    },
    [isValid, navigate, setError, setToken]
  );

  const handleLogout = useCallback(() => {
    Storage.remove(StorageKey.TOKEN);
    navigate(AppRoutes.logout, { replace: true });
  }, [navigate]);

  return (
    <form className="contents" onSubmit={handleSubmit(onSubmit)}>
      <Layout
        className="h-full text-center"
        showTitle
        showPrivacyModal
        toolbar={
          <>
            <Button disabled={!isValid || processing} type="submit">
              {processing ? "Loading ..." : "Continue"}
            </Button>

            <Button
              type="button"
              onClick={handleLogout}
              variant={ButtonVariant.FLAT}
            >
              Log out
            </Button>
          </>
        }
      >
        <div className="grid h-full space-y-12">
          <div className="grid self-start justify-items-center">
            <Ai />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Welcome {userName}!
            </h1>

            <p className="text-858494 mt-3">
              Thanks for helping to improve our machine learning algorithms!
            </p>

            <p className="text-858494 mt-4 text-m">
              To access the tasks and contribute training data, please enter a
              valid campaign passphrase provided by your campaign manager.
            </p>
          </div>

          <div className="text-left space-y-8">
            <Field
              label="Enter campaign passphrase"
              description={errors.passphrase?.message}
              descriptionClassName={
                errors.passphrase?.message ? "text-fd380f" : undefined
              }
              as="label"
            >
              <InputPassword
                disabled={processing}
                type="password"
                id="passphrase"
                autoCapitalize="off"
                autoComplete="off"
                autoFocus
                {...register("passphrase", { required: true })}
              />
            </Field>
          </div>
        </div>
      </Layout>
    </form>
  );
});
